import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import GameCard from './GameCard';
import { useAuth } from '../utils/AuthContext';

const Calendar = () => {
  const [gamesByDate, setGamesByDate] = useState({});
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [chronologicalGames, setChronologicalGames] = useState([]);
  const [showTopButton, setShowTopButton] = useState(false);
  const { user } = useAuth();

  const [filters, setFilters] = useState({
    show_tracked: true,
    age_rating: '',
    language_support: '',
    genre: '',
    theme: '',
    game_mode: '',
    player_perspective: '',
  });

  const [ageRatings, setAgeRatings] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [genres, setGenres] = useState([]);
  const [themes, setThemes] = useState([]);
  const [gameModes, setGameModes] = useState([]);
  const [playerPerspectives, setPlayerPerspectives] = useState([]);

  const [loaded, setLoaded] = useState({
    ageRatings: false,
    languages: false,
    genres: false,
    themes: false,
    gameModes: false,
    playerPerspectives: false,
  });

  useEffect(() => {
    fetchGames();
  }, [currentMonth, filters]);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      setShowTopButton(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const fetchGames = async () => {
    try {
      const monthString = currentMonth.toISOString().slice(0, 7);
      const params = {
        month: monthString,
        ...filters,
        show_tracked: filters.show_tracked.toString(),
        user_id: user?.id || 0
      };

      const response = await api.get('/calendar', { params });
      setGamesByDate(response.data);

      // Create the chronological list directly from the calendar data
      const allGames = [];
      // Get all dates for the current month
      const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      
      // Iterate through each day of the month
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        // Format date as YYYY-MM-DD without timezone conversion
        const dateString = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (response.data[dateString]) {
          response.data[dateString].forEach(game => {
            allGames.push({
              id: game.id,
              title: game.name,
              releaseDateUnix: game.releaseDateUnix,
              releaseDate: new Date(game.releaseDateUnix * 1000).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                timeZone: 'UTC'
              }),
              cover: game.cover || "/StockGameCover.jpg",
              summary: game.summary || "No synopsis available.",
              trailerUrl: game.trailerUrl
                ? game.trailerUrl.replace("watch?v=", "embed/")
                : null,
              date: dateString,
              isTracked: game.isTracked
            });
          });
        }
      }

      setChronologicalGames(allGames);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  };

  const startOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const endOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const daysInMonth = endOfMonth.getDate();

  const scrollToGame = (gameId) => {
    const element = document.getElementById(`game-${gameId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a temporary highlight effect
      element.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50');
      setTimeout(() => {
        element.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50');
      }, 2000);
    }
  };

  const generateCalendarDays = () => {
    const days = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${currentMonth.getFullYear()}-${String(
        currentMonth.getMonth() + 1
      ).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

      days.push(
        <div key={day} className="p-2 border border-gray-300 h-32 overflow-auto">
          <div className="font-bold">{day}</div>
          {gamesByDate[dateString]?.map((game, idx) => (
            <div key={idx} className="relative group">
              <div 
                className="text-xs mt-1 cursor-pointer hover:text-blue-500"
                onClick={() => scrollToGame(game.id)}
              >
                {game.name}
              </div>
            </div>
          ))}
        </div>
      );
    }
    return days;
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const lazyLoad = async (type) => {
    if (loaded[type]) return;

    const endpointMap = {
      ageRatings: '/calendar-filters/age-ratings',
      languages: '/calendar-filters/language-supports',
      genres: '/calendar-filters/genres',
      themes: '/calendar-filters/themes',
      gameModes: '/calendar-filters/game-modes',
      playerPerspectives: '/calendar-filters/player-perspectives',
    };

    try {
      const response = await api.get(endpointMap[type]);
      switch (type) {
        case 'ageRatings':
          setAgeRatings(response.data);
          break;
        case 'languages':
          setLanguages(response.data);
          break;
        case 'genres':
          setGenres(response.data);
          break;
        case 'themes':
          setThemes(response.data);
          break;
        case 'gameModes':
          setGameModes(response.data);
          break;
        case 'playerPerspectives':
          setPlayerPerspectives(response.data);
          break;
        default:
          break;
      }
      setLoaded((prev) => ({ ...prev, [type]: true }));
    } catch (err) {
      console.error(`Failed to load ${type}:`, err);
    }
  };

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Add a function to group games by date
  const groupGamesByDate = (games) => {
    return games.reduce((groups, game) => {
      const date = game.date;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(game);
      return groups;
    }, {});
  };

  // Add a function to format the date header
  const formatDateHeader = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="p-4 flex flex-col lg:flex-row gap-6">
      {/* Sidebar Filters */}
      <div className="w-full lg:w-60 flex flex-col gap-4">
        <h3 className="text-lg font-semibold text-white mb-2">Filter Releases</h3>

        {/* Tracked Games Toggle */}
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-white">Include Tracked Games</label>
          <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out rounded-full bg-blue-200">
            <input
              type="checkbox"
              name="show_tracked"
              checked={filters.show_tracked}
              onChange={(e) => setFilters({ ...filters, show_tracked: e.target.checked })}
              className="absolute w-6 h-6 transition duration-100 ease-in-out transform bg-white border-4 rounded-full appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 checked:translate-x-6 border-gray-300 checked:border-blue-600 checked:bg-blue-600"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-1">Age Rating</label>
          <select
            name="age_rating"
            value={filters.age_rating}
            onChange={handleFilterChange}
            onClick={() => lazyLoad('ageRatings')}
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-800"
          >
            <option value="">All</option>
            {ageRatings.map((rating) => (
              <option key={rating.id} value={rating.id}>
                {rating.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-1">Language Support</label>
          <select
            name="language_support"
            value={filters.language_support}
            onChange={handleFilterChange}
            onClick={() => lazyLoad('languages')}
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-800"
          >
            <option value="">All</option>
            {languages.map((lang) => (
              <option key={lang.id} value={lang.id}>
                {lang.language}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-1">Genre</label>
          <select
            name="genre"
            value={filters.genre}
            onChange={handleFilterChange}
            onClick={() => lazyLoad('genres')}
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-800"
          >
            <option value="">All</option>
            {genres.map((genre) => (
              <option key={genre.id} value={genre.id}>
                {genre.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-1">Theme</label>
          <select
            name="theme"
            value={filters.theme}
            onChange={handleFilterChange}
            onClick={() => lazyLoad('themes')}
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-800"
          >
            <option value="">All</option>
            {themes.map((theme) => (
              <option key={theme.id} value={theme.id}>
                {theme.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-1">Game Mode</label>
          <select
            name="game_mode"
            value={filters.game_mode}
            onChange={handleFilterChange}
            onClick={() => lazyLoad('gameModes')}
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-800"
          >
            <option value="">All</option>
            {gameModes.map((mode) => (
              <option key={mode.id} value={mode.id}>
                {mode.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-white mb-1">Player Perspective</label>
          <select
            name="player_perspective"
            value={filters.player_perspective}
            onChange={handleFilterChange}
            onClick={() => lazyLoad('playerPerspectives')}
            className="w-full p-2 border border-gray-300 rounded bg-white text-gray-800"
          >
            <option value="">All</option>
            {playerPerspectives.map((perspective) => (
              <option key={perspective.id} value={perspective.id}>
                {perspective.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Calendar and List */}
      <div className="flex-1">
        <div className="flex justify-between mb-4">
          <button
            onClick={handlePrevMonth}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Previous
          </button>
          <h2 className="text-xl font-bold">
            {currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
          </h2>
          <button
            onClick={handleNextMonth}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Next
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-6">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center font-bold p-2">
              {day}
            </div>
          ))}
          {generateCalendarDays()}
        </div>

        {/* Chronological List */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-blue-600 mb-4">Upcoming Releases</h3>
          <div className="space-y-8">
            {chronologicalGames.length > 0 ? (
              Object.entries(groupGamesByDate(chronologicalGames))
                .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                .map(([date, games]) => (
                  <div key={date} className="space-y-4">
                    <h4 className="text-lg font-semibold text-white border-b pb-2">
                      {formatDateHeader(date)}
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {games.map((game) => (
                        <div key={game.id} id={`game-${game.id}`}>
                          <GameCard 
                            game={game} 
                            variant="dark" 
                            showRating={false}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No games found matching the current filters
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top of Page Button */}
      {showTopButton && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg transition-all duration-300 flex items-center gap-2 z-50"
        >
          <svg 
            className="w-5 h-5" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M5 10l7-7m0 0l7 7m-7-7v18" 
            />
          </svg>
          Top of Page
        </button>
      )}
    </div>
  );
};

export default Calendar;
